import type {
	DocumentType,
	HumanProcessingStatus,
	IssuerType,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { constructionSiteAnalysisQueryKeys } from "../query-keys/constructionSiteAnalysis.query-keys";
import { constructionSiteReceptionQueryKeys } from "../query-keys/constructionSiteReception.query-keys";
import { constructionSiteSubmissionQueryKeys } from "../query-keys/constructionSiteSubmission.query-keys";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";
import { filesQueryKeys } from "../query-keys/files.query-keys";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";

type FileType = { file: File; url: string };

const postFileOnAWSS3 = async (fileType: FileType) => {
	const data = await axios.put(fileType.url, fileType.file, {
		headers: {
			"Content-Type": fileType.file.type,
		},
	});

	return data;
};

export const usePostFileOnAWSS3 = () => {
	return useMutation({
		mutationFn: postFileOnAWSS3,
		onSuccess: () => {
			toast.success("Fichier téléchargé avec succès");
		},
		onError: (error: unknown) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors du téléchargement du fichier", {
				description: errorMessage,
			});
		},
	});
};

type UpdateFileMetadataBody = {
	mutableMetadata: unknown;
	comment: string;
};

export const updateFileMetadata = async (
	fileId: string,
	body: UpdateFileMetadataBody,
) => {
	const { data } = await privateAxios.patch(`/files/${fileId}/metadata`, body);
	return data;
};

export const useUpdateFileMetadata = (fileId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateFileMetadataBody) =>
			updateFileMetadata(fileId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: filesQueryKeys.fileMetadataDetails(fileId),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSiteAnalysisQueryKeys.all,
			});
			toast.success("Métadonnées enregistrées");
		},
		onError: (error: unknown) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur", {
				description: errorMessage,
			});
		},
	});
};

export const updateHumanProcessingStatus = async (
	fileId: string,
	humanProcessingStatus: HumanProcessingStatus,
) => {
	const { data } = await privateAxios.patch(
		`/files/${fileId}/human-processing-status`,
		{
			humanProcessingStatus,
		},
	);
	return data;
};

export const useUpdateHumanProcessingStatus = (fileId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (humanProcessingStatus: HumanProcessingStatus) =>
			updateHumanProcessingStatus(fileId, humanProcessingStatus),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: constructionSiteSubmissionQueryKeys.details(),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.all,
			});
			// Refresh analysis step lists
			queryClient.invalidateQueries({
				queryKey: constructionSiteAnalysisQueryKeys.all,
			});
			// Refresh proposition documents lists to update badges (partial key match)
			queryClient.invalidateQueries({
				queryKey: [...propositionsQueryKeys.all, "documents"],
			});
			toast.success("Statut de traitement mis à jour avec succès");
		},
		onError: (error: unknown) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour du statut", {
				description: errorMessage,
			});
		},
	});
};

export const renameFile = async (fileId: string, name: string) => {
	const { data } = await privateAxios.patch(`/files/${fileId}/name`, {
		name,
	});
	return data;
};

export const useRenameFile = (fileId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (name: string) => renameFile(fileId, name),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: filesQueryKeys.fileWithMetadata(fileId),
			});
			queryClient.invalidateQueries({
				queryKey: filesQueryKeys.filesWithMetadata([fileId]),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSiteSubmissionQueryKeys.details(),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSiteAnalysisQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: constructionSiteReceptionQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: [...propositionsQueryKeys.all, "documents"],
			});
			toast.success("Fichier renommé avec succès");
		},
		onError: (error: unknown) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors du renommage du fichier", {
				description: errorMessage,
			});
		},
	});
};

export const updateFileTypeAndIssuer = async (
	fileId: string,
	fileType: DocumentType,
	issuerType?: IssuerType,
) => {
	const { data } = await privateAxios.patch(`/files/${fileId}/type`, {
		fileType,
		issuerType,
	});
	return data;
};

export const useUpdateFileTypeAndIssuerMutation = (fileId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			fileType,
			issuerType,
		}: { fileType: DocumentType; issuerType?: IssuerType }) =>
			updateFileTypeAndIssuer(fileId, fileType, issuerType),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: filesQueryKeys.fileWithMetadata(fileId),
			});
			queryClient.invalidateQueries({
				queryKey: constructionSiteSubmissionQueryKeys.all,
			});
			// Refresh analysis step lists
			queryClient.invalidateQueries({
				queryKey: constructionSiteAnalysisQueryKeys.all,
			});
			toast.success("Type de fichier et émetteur mis à jour avec succès");
		},
		onError: (error: unknown) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour du type de fichier", {
				description: errorMessage,
			});
		},
	});
};
